import React from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    useTheme,
} from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';

interface Problem {
    icon: React.ReactNode;
    title: string;
    description: string;
    accent: string;
}

const ProblemSection: React.FC = () => {
    const theme = useTheme();

    const problems: Problem[] = [
        // Before the trip
        {
            icon: <HelpOutlineOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Who Do You Actually Trust?',
            description: 'Generic reviews are written by strangers with different budgets, tastes, and travel styles. The people who know what you\'d love? They\'re in your contacts — not on a review site.',
            accent: '#2D6E6A',
        },
        {
            icon: <SearchOffOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Hours of Research, Still Not Sure',
            description: 'Endless tabs, contradicting blog posts, outdated recommendations. You can spend days researching and still wonder if you\'re making the right call.',
            accent: '#2D6E6A',
        },
        // During the trip
        {
            icon: <LayersOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Your Trip Lives in a Dozen Places',
            description: 'Confirmations in email, notes in one app, photos in another. There\'s no single place where your actual trip lives — so nothing is where you need it.',
            accent: '#C17B3E',
        },
        {
            icon: <CloudOffOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Moments Happen Fast. Context Gets Lost.',
            description: 'You take a photo, but six months later you can\'t remember which day it was, who was there, or exactly where you were. The memory fades even when the photo doesn\'t.',
            accent: '#C17B3E',
        },
        // After the trip
        {
            icon: <PhotoOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Photos Without a Story',
            description: 'Thousands of photos buried in your camera roll with no context. You know you loved that trip — but the details are already slipping away.',
            accent: '#4A6FA5',
        },
        {
            icon: <GroupsOutlinedIcon sx={{ fontSize: '2.2rem' }} />,
            title: 'Your Experience Stays With You',
            description: 'When a friend asks "have you been to Tokyo?" you wish you could share everything — not just a few texts. Your hard-won knowledge deserves a better home.',
            accent: '#4A6FA5',
        },
    ];

    return (
        <Box
            sx={{
                py: { xs: 8, md: 12 },
                background: theme.palette.background.paper,
            }}
        >
            <Container maxWidth="lg">
                {/* Section Header */}
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '2rem', md: '2.75rem' },
                            fontWeight: 'bold',
                            color: 'text.primary',
                            mb: 2,
                        }}
                    >
                        Travel Has a Trust Problem
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'text.secondary',
                            maxWidth: '680px',
                            mx: 'auto',
                            fontSize: { xs: '1rem', md: '1.15rem' },
                            lineHeight: 1.6,
                        }}
                    >
                        Before, during, and after every trip — the tools people rely on weren't built for how they actually travel.
                    </Typography>
                </Box>

                {/* Problems Grid */}
                <Grid container spacing={4}>
                    {problems.map((problem, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: '20px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                                    transition: 'all 0.3s ease',
                                    borderTop: `4px solid ${problem.accent}`,
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 40px rgba(0,0,0,0.11)',
                                    },
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    {/* Icon */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '70px',
                                            height: '70px',
                                            background: 'transparent',
                                            border: `2px solid ${problem.accent}`,
                                            borderRadius: '16px',
                                            mb: 3,
                                            color: problem.accent,
                                        }}
                                    >
                                        {problem.icon}
                                    </Box>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'text.primary',
                                            mb: 1.5,
                                            fontSize: '1.15rem',
                                        }}
                                    >
                                        {problem.title}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: 'text.secondary',
                                            lineHeight: 1.7,
                                            fontSize: '0.95rem',
                                        }}
                                    >
                                        {problem.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default ProblemSection;
